/* Bande « composition » : les onglets de sélection.

   Portée locale, comme le reste de la refonte : une page peut poser la section
   plusieurs fois (aperçus, éditeur), et deux exécutions d'un même `class` en
   portée globale lèvent « already been declared ». */
(() => {
  // Aération laissée entre la barre collée et le haut du panneau recadré.
  const MARGE = 12;

  class Composition {
    constructor(racine) {
      this.racine = racine;
      this.chips = [...racine.querySelectorAll('[data-compo-chip]')];
      this.panneaux = [...racine.querySelectorAll('[data-compo-panneau]')];
      this.barre = racine.querySelector('.ltb-compo__chips');
      // Le collage est purement CSS : il vaut même pour une box à une seule
      // sélection, où il n'y a rien à brancher. L'ombre doit suivre.
      this.surveillerCollage();
      if (this.chips.length < 2) return;
      this.brancher();
    }

    brancher() {
      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => this.montrer(chip.dataset.compoChip));
      });

      // Navigation au clavier attendue d'un `role="tablist"` : les flèches
      // déplacent la sélection, la tabulation sort du groupe.
      this.racine.querySelector('[role="tablist"]').addEventListener('keydown', (e) => {
        const i = this.chips.indexOf(document.activeElement);
        if (i === -1) return;
        let suivant = null;
        if (e.key === 'ArrowRight') suivant = this.chips[i + 1] || this.chips[0];
        if (e.key === 'ArrowLeft') suivant = this.chips[i - 1] || this.chips[this.chips.length - 1];
        if (e.key === 'Home') suivant = this.chips[0];
        if (e.key === 'End') suivant = this.chips[this.chips.length - 1];
        if (!suivant) return;
        e.preventDefault();
        this.montrer(suivant.dataset.compoChip);
        suivant.focus();
      });
    }

    // La barre d'onglets est collée en haut sur mobile, et son ombre ne doit
    // se poser qu'une fois qu'elle l'est vraiment. `threshold: [1]` le voit,
    // à condition d'écarter le cas symétrique : la barre sort aussi par le bas
    // de la fenêtre tant que la section est sous la ligne de flottaison, et le
    // ratio y passe également sous 1 sans qu'elle soit collée.
    surveillerCollage() {
      if (!this.barre || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(
        ([entree]) => {
          const collee = entree.intersectionRatio < 1 && entree.boundingClientRect.top <= 0;
          this.barre.classList.toggle('is-collee', collee);
        },
        { threshold: [1] },
      ).observe(this.barre);
    }

    // Changer d'onglet depuis une barre collée laissait le lecteur dans le
    // vide : sur Venise « Sélection Vrac » fait 1231 px et « Gourmandise »
    // 533, si bien que le défilement pointait au-delà de la section une fois
    // le panneau court affiché. On ramène le début du nouveau panneau juste
    // sous la barre, sans animation : l'utilisateur a demandé un contenu, pas
    // un voyage.
    recadrer(panneau) {
      if (!this.barre || !panneau) return;
      // Le panneau qui s'efface raccourcit la page, et le navigateur ramène de
      // lui-même le défilement dans les limites du nouveau document. Mesurer
      // avant ce recalage donnait une cible fausse de 170 px, et le panneau
      // restait à cheval sur le haut de l'écran. D'où deux précautions : on
      // attend la trame suivante, et on raisonne en position absolue dans le
      // document plutôt qu'en écart au défilement courant.
      requestAnimationFrame(() => {
        const haut = panneau.getBoundingClientRect().top + window.scrollY;
        const cible = haut - this.barre.getBoundingClientRect().height - MARGE;
        // Le début du panneau est déjà à l'écran : rien à corriger.
        if (window.scrollY <= cible) return;
        window.scrollTo({ top: cible, behavior: 'instant' });
      });
    }

    montrer(index) {
      this.chips.forEach((chip) => {
        const actif = chip.dataset.compoChip === index;
        chip.classList.toggle('is-selected', actif);
        chip.setAttribute('aria-selected', actif ? 'true' : 'false');
        chip.tabIndex = actif ? 0 : -1;
      });
      this.panneaux.forEach((panneau) => {
        panneau.hidden = panneau.dataset.compoPanneau !== index;
      });
      this.recadrer(this.panneaux.find((p) => p.dataset.compoPanneau === index));
    }
  }

  const demarrer = (scope = document) => {
    scope.querySelectorAll('.ltb-compo').forEach((racine) => {
      if (racine.dataset.ltbCompoPret) return;
      racine.dataset.ltbCompoPret = '1';
      new Composition(racine);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => demarrer());
  } else {
    demarrer();
  }

  document.addEventListener('shopify:section:load', (e) => demarrer(e.target));
})();
